// src/pages/eventRequests/requestList/requestList.js
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Button, DatePicker, Modal, Pagination, Select, Table, Tooltip } from "antd";
import { FaEye } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import "./requestList.css";

const { RangePicker } = DatePicker;

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  requestData: [],
  currentlyEditingId: null,
  currentPage: 1,
  pageSize: 10,
  sortConfig: null,
  showDateRangeModal: false,
  tempFromDate: null,
  tempToDate: null,
  fromDate: null,
  toDate: null,
  dateRangeType: null // 'application' or 'event'
};

// Reducer for state management
const requestReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setRequestData":
      return { ...state, requestData: action.payload };
    case "setEditingId":
      return { ...state, currentlyEditingId: action.payload };
    case "updateRequestStatus":
      return {
        ...state,
        requestData: state.requestData.map((item) => (item.id === action.payload.id ? { ...item, status: action.payload.status } : item)),
        currentlyEditingId: null
      };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload, currentPage: 1 };
    case "setDateRangeModal":
      return {
        ...state,
        showDateRangeModal: action.payload.show,
        dateRangeType: action.payload.type || null
      };
    case "setTempDateRange":
      return {
        ...state,
        tempFromDate: action.payload.from,
        tempToDate: action.payload.to
      };
    case "setDateRange":
      return {
        ...state,
        fromDate: action.payload.from,
        toDate: action.payload.to
      };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1,
        fromDate: null,
        toDate: null,
        tempFromDate: null,
        tempToDate: null,
        showDateRangeModal: false,
        dateRangeType: null
      };
    default:
      return state;
  }
};

function RequestList() {
  const { notification, showNotification, hideNotification } = useNotification();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [state, dispatch] = useReducer(requestReducer, initialState);

  // State for immediate search display
  const [displaySearchTerm, setDisplaySearchTerm] = useState("");

  const {
    isLoading,
    searchTerm,
    filters,
    requestData,
    currentlyEditingId,
    currentPage,
    pageSize,
    sortConfig,
    showDateRangeModal,
    tempFromDate,
    tempToDate,
    fromDate,
    toDate,
    dateRangeType
  } = state;

  // Configuration objects
  const filtersConfig = useMemo(
    () => ({
      applicationDate: {
        label: "Application Date",
        options: [
          { value: "last7Days", label: "Last 7 Days" },
          { value: "last30Days", label: "Last 30 Days" },
          { value: "dateRange", label: "Custom Date Range" }
        ]
      },
      eventDate: {
        label: "Event Date",
        options: [
          { value: "next7Days", label: "Next 7 Days" },
          { value: "next30Days", label: "Next 30 Days" },
          { value: "dateRange", label: "Custom Date Range" }
        ]
      },
      requestType: {
        label: "Request Type",
        options: [
          { value: "childDedication", label: "Child Dedication" },
          { value: "baptism", label: "Baptism" },
          { value: "engagement", label: "Engagement" },
          { value: "wedding", label: "Wedding" },
          { value: "houseDedication", label: "House Dedication" }
        ]
      },
      eventStatus: {
        label: "Event Status",
        options: [
          { value: "toBeReviewed", label: "To be reviewed" },
          { value: "inProgress", label: "In-Progress" },
          { value: "approved", label: "Approved" },
          { value: "followUp", label: "Follow-Up" },
          { value: "completed", label: "Completed" }
        ]
      }
    }),
    []
  );

  const statusOptions = useMemo(
    () => [
      { value: "toBeReviewed", label: "To be reviewed" },
      { value: "inProgress", label: "In-Progress" },
      { value: "approved", label: "Approved" },
      { value: "followUp", label: "Follow-Up" },
      { value: "completed", label: "Completed" }
    ],
    []
  );

  const mappings = useMemo(
    () => ({
      eventTypeLabels: {
        child_dedication: "Child Dedication",
        baptism: "Baptism",
        engagement: "Engagement",
        wedding: "Wedding",
        house_dedication: "House Dedication"
      },
      requestTypeMapping: {
        childDedication: "child_dedication",
        baptism: "baptism",
        engagement: "engagement",
        wedding: "wedding",
        houseDedication: "house_dedication"
      },
      statusMapping: {
        toBeReviewed: "To be reviewed",
        inProgress: "In-Progress",
        approved: "Approved",
        followUp: "Follow-Up",
        completed: "Completed"
      }
    }),
    []
  );

  // Utility functions for status colors
  const getStatusColors = useCallback((status) => {
    const colorMap = {
      Approved: { bg: "#C8E6C9", text: "#2E7D32" },
      "Follow-Up": { bg: "#FFCDD2", text: "#C62828" },
      "In-Progress": { bg: "#FFF9C4", text: "#F9A825" },
      "To be reviewed": { bg: "#E1BEE7", text: "#6A1B9A" },
      Completed: { bg: "#D1C4E9", text: "#3E3A89" }
    };
    return colorMap[status] || { bg: "#ECEFF1", text: "#455A64" };
  }, []);

  // Date filtering utility
  const filterByDate = useCallback(
    (items, dateFilter, dateField) => {
      if (!dateFilter) return items;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

      switch (dateFilter) {
        case "last7Days": {
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(today.getDate() - 7);
          return items.filter((item) => {
            const itemDate = new Date(item[dateField]);
            return itemDate >= sevenDaysAgo && itemDate <= now;
          });
        }
        case "last30Days": {
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          return items.filter((item) => {
            const itemDate = new Date(item[dateField]);
            return itemDate >= thirtyDaysAgo && itemDate <= now;
          });
        }
        case "next7Days": {
          const sevenDaysFromNow = new Date(today);
          sevenDaysFromNow.setDate(today.getDate() + 7);
          sevenDaysFromNow.setHours(23, 59, 59, 999); // End of the 7th day
          return items.filter((item) => {
            const itemDate = new Date(item[dateField]);
            return itemDate >= today && itemDate <= sevenDaysFromNow;
          });
        }
        case "next30Days": {
          const thirtyDaysFromNow = new Date(today);
          thirtyDaysFromNow.setDate(today.getDate() + 30);
          thirtyDaysFromNow.setHours(23, 59, 59, 999); // End of the 30th day
          return items.filter((item) => {
            const itemDate = new Date(item[dateField]);
            return itemDate >= today && itemDate <= thirtyDaysFromNow;
          });
        }
        case "dateRange": {
          if (fromDate && toDate) {
            const from = new Date(fromDate);
            from.setHours(0, 0, 0, 0); // Start of day
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999); // End of day
            return items.filter((item) => {
              const date = new Date(item[dateField]);
              return date >= from && date <= to;
            });
          }
          return items;
        }
        default:
          return items;
      }
    },
    [fromDate, toDate]
  );

  // Global search function
  const globalSearch = useCallback(
    (items, term) => {
      if (!term?.trim()) return items;

      const searchLower = term.toLowerCase().trim();

      return items.filter((item) => {
        const name = item.name || `${item.groom || ""} ${item.bride || ""}`.trim();
        const contact = `${item.isd_code || ""} ${item.phone || ""} ${item.email || ""}`.trim();
        const requestType = mappings.eventTypeLabels[item.event_type] || item.event_type || "";

        const applicationDate = item.created_at
          ? new Date(item.created_at)
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              })
              .replace(/ /g, "-")
          : "";

        const eventDate = item.event_date
          ? new Date(item.event_date)
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              })
              .replace(/ /g, "-")
          : "";

        const searchFields = [name, contact, requestType, applicationDate, eventDate, item.status || ""];

        return searchFields.some((field) => field.toLowerCase().includes(searchLower));
      });
    },
    [mappings.eventTypeLabels]
  );

  // Apply all filters, search, and sorting in correct order
  const filteredData = useMemo(() => {
    if (!Array.isArray(requestData)) {
      return [];
    }

    let result = [...requestData];

    // Step 1: Apply application date filter
    result = filterByDate(result, filters.applicationDate, "created_at");

    // Step 2: Apply event date filter
    result = filterByDate(result, filters.eventDate, "event_date");

    // Step 3: Apply request type filter
    if (filters.requestType) {
      const dbType = mappings.requestTypeMapping[filters.requestType];
      result = result.filter((item) => item.event_type === dbType);
    }

    // Step 4: Apply event status filter
    if (filters.eventStatus) {
      const status = mappings.statusMapping[filters.eventStatus];
      result = result.filter((item) => item.status === status);
    }

    // Step 5: Apply global search
    result = globalSearch(result, searchTerm);

    // Step 6: Apply sorting BEFORE pagination (CRITICAL FIX)
    if (sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;

        if (key === "created_at" || key === "event_date") {
          const dateA = new Date(a[key]);
          const dateB = new Date(b[key]);
          return direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        if (key === "name") {
          const nameA = a.name || `${a.groom || ""} ${a.bride || ""}`.trim();
          const nameB = b.name || `${b.groom || ""} ${b.bride || ""}`.trim();
          return direction === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        }

        if (key === "event_type") {
          const typeA = mappings.eventTypeLabels[a.event_type] || a.event_type || "";
          const typeB = mappings.eventTypeLabels[b.event_type] || b.event_type || "";
          return direction === "asc" ? typeA.localeCompare(typeB) : typeB.localeCompare(typeA);
        }

        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";

        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    // Return fully processed data (filtered, searched, and sorted)
    return result;
  }, [requestData, filters, searchTerm, filterByDate, mappings, globalSearch, sortConfig]);

  // Get current page data (pagination happens AFTER all processing)
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  // Event handlers
  const handleStatusClick = useCallback((id) => {
    dispatch({ type: "setEditingId", payload: id });
  }, []);

  const handleStatusChange = useCallback(
    async (value, rowId) => {
      const selectedOption = statusOptions.find((opt) => opt.value === value);
      const newStatus = selectedOption.label;

      try {
        const token = await getAuthToken();
        const response = await axios.patch(
          API_ENDPOINTS.EVENT_REQUESTS,
          { action: "updateStatus", id: rowId, status: newStatus },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.status === 200) {
          dispatch({
            type: "updateRequestStatus",
            payload: { id: rowId, status: newStatus }
          });
          showNotification("Status updated successfully!", "success");
        } else {
          showNotification("Failed to update status.", "error");
        }
      } catch (error) {
        console.error("Status update error:", error);
        dispatch({ type: "setEditingId", payload: null });
        showNotification("Failed to update status.", "error");
      }
    },
    [statusOptions, showNotification]
  );

  // Debounced search dispatch
  const debouncedDispatch = useMemo(
    () =>
      debounce((term) => {
        dispatch({ type: "setSearchTerm", payload: term });
      }, 300),
    []
  );

  const handleSearch = useCallback(
    (term) => {
      setDisplaySearchTerm(term);
      debouncedDispatch(term);
    },
    [debouncedDispatch]
  );

  const handleFilterChange = useCallback(
    (newFilters) => {
      // Check if Application Date Custom Date Range was selected
      if (newFilters.applicationDate === "dateRange" && filters.applicationDate !== "dateRange") {
        dispatch({
          type: "setDateRangeModal",
          payload: { show: true, type: "application" }
        });
        return;
      }

      // Check if Event Date Custom Date Range was selected
      if (newFilters.eventDate === "dateRange" && filters.eventDate !== "dateRange") {
        dispatch({
          type: "setDateRangeModal",
          payload: { show: true, type: "event" }
        });
        return;
      }

      // Clear date range data if switching away from Application Date range
      if (filters.applicationDate === "dateRange" && newFilters.applicationDate !== "dateRange") {
        dispatch({
          type: "setDateRange",
          payload: { from: null, to: null }
        });
        dispatch({
          type: "setTempDateRange",
          payload: { from: null, to: null }
        });
      }

      // Clear date range data if switching away from Event Date range
      if (filters.eventDate === "dateRange" && newFilters.eventDate !== "dateRange") {
        dispatch({
          type: "setDateRange",
          payload: { from: null, to: null }
        });
        dispatch({
          type: "setTempDateRange",
          payload: { from: null, to: null }
        });
      }

      dispatch({ type: "setFilters", payload: newFilters });
    },
    [filters]
  );

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
    setDisplaySearchTerm("");
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  const handleDateRangeChange = useCallback((dates, dateStrings) => {
    dispatch({
      type: "setTempDateRange",
      payload: {
        from: dateStrings[0] || null,
        to: dateStrings[1] || null
      }
    });
  }, []);

  const applyDateRangeFilter = useCallback(() => {
    if (tempFromDate && tempToDate) {
      dispatch({
        type: "setDateRange",
        payload: { from: tempFromDate, to: tempToDate }
      });

      // Apply filter based on which date type was selected
      if (dateRangeType === "application") {
        dispatch({
          type: "setFilters",
          payload: { ...filters, applicationDate: "dateRange" }
        });
      } else if (dateRangeType === "event") {
        dispatch({
          type: "setFilters",
          payload: { ...filters, eventDate: "dateRange" }
        });
      }

      dispatch({ type: "setDateRangeModal", payload: { show: false } });
    } else {
      alert("Please select both from and to dates");
    }
  }, [tempFromDate, tempToDate, filters, dateRangeType]);

  const handleDateRangeModalCancel = useCallback(() => {
    dispatch({ type: "setDateRangeModal", payload: { show: false } });
    dispatch({
      type: "setTempDateRange",
      payload: { from: null, to: null }
    });

    // Clear the specific date filter that triggered the modal
    const updatedFilters = { ...filters };
    if (dateRangeType === "application") {
      delete updatedFilters.applicationDate;
    } else if (dateRangeType === "event") {
      delete updatedFilters.eventDate;
    }
    dispatch({ type: "setFilters", payload: updatedFilters });
  }, [filters, dateRangeType]);

  // Handle table change (sorting)
  const handleTableChange = useCallback((pagination, filters, sorter) => {
    if (sorter.order) {
      dispatch({
        type: "setSortConfig",
        payload: {
          key: sorter.field,
          direction: sorter.order === "ascend" ? "asc" : "desc"
        }
      });
    } else {
      dispatch({
        type: "setSortConfig",
        payload: null
      });
    }
  }, []);

  // Table columns configuration
  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      {
        title: "NAME",
        dataIndex: "name",
        sorter: true,
        sortOrder: sortConfig?.key === "name" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (_, record) => {
          const name = record.name || `${record.groom || ""} & ${record.bride || ""}`.trim();
          return name || "-";
        }
      },
      {
        title: "CONTACT INFO",
        render: (record) => {
          const contact = `${record.isd_code || ""} ${record.phone || record.email || ""}`.trim();
          return contact || "-";
        }
      },
      {
        title: "REQUEST TYPE",
        dataIndex: "event_type",
        sorter: true,
        sortOrder: sortConfig?.key === "event_type" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (type) => mappings.eventTypeLabels[type] || type || "-"
      },
      {
        title: "APPLICATION DATE",
        dataIndex: "created_at",
        sorter: true,
        sortOrder: sortConfig?.key === "created_at" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (date) =>
          date
            ? new Date(date)
                .toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric"
                })
                .replace(/ /g, "-")
            : "-"
      },
      {
        title: "EVENT DATE",
        dataIndex: "event_date",
        sorter: true,
        sortOrder: sortConfig?.key === "event_date" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (date) =>
          date
            ? new Date(date)
                .toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric"
                })
                .replace(/ /g, "-")
            : "-"
      },
      {
        title: "STATUS",
        dataIndex: "status",
        sorter: true,
        sortOrder: sortConfig?.key === "status" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (_, record) => {
          if (currentlyEditingId === record.id) {
            return (
              <div ref={dropdownRef}>
                <Select
                  value={statusOptions.find((opt) => opt.label === record.status)?.value}
                  onChange={(value) => handleStatusChange(value, record.id)}
                  style={{ width: "10em" }}
                  options={statusOptions}
                  autoFocus
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </div>
            );
          }

          const colors = getStatusColors(record.status);
          return (
            <span
              className="rql-status-text"
              onClick={() => handleStatusClick(record.id)}
              style={{
                backgroundColor: colors.bg,
                color: colors.text,
                cursor: "pointer"
              }}
            >
              {record.status || "-"}
            </span>
          );
        }
      },
      {
        title: "ACTIONS",
        render: (_, record) => (
          <div className="rql-action-buttons" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="View">
              <button className="rql-table-view-btn" onClick={() => navigate("/requestDetails", { state: { item: record } })}>
                <FaEye />
              </button>
            </Tooltip>
          </div>
        ),
        width: 80
      }
    ],
    [currentlyEditingId, sortConfig, handleStatusClick, handleStatusChange, navigate, mappings, statusOptions, getStatusColors, currentPage, pageSize]
  );

  // Excel export data
  const prepareExcelData = useCallback(() => {
    return filteredData.map((request) => ({
      "Request ID": request.id || request.request_id || request.event_id,
      Name: request.name || `${request.groom || ""} & ${request.bride || ""}`.trim(),
      "Contact Info": `${request.isd_code || ""} ${request.phone || request.email || ""}`.trim(),
      "Request Type": mappings.eventTypeLabels[request.event_type] || request.event_type,
      "Application Date": request.created_at,
      "Event Date": request.event_date,
      Status: request.status
    }));
  }, [filteredData, mappings]);

  // Effects
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        dispatch({ type: "setEditingId", payload: null });
      }
    };

    if (currentlyEditingId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [currentlyEditingId]);

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      try {
        dispatch({ type: "setLoading", payload: true });

        const token = await getAuthToken();

        const response = await axios.get(API_ENDPOINTS.EVENT_REQUESTS, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!isMounted) return;

        const items = Array.isArray(response.data?.eventReqs)
          ? response.data.eventReqs
          : Array.isArray(response.data?.eventReqs?.Items)
            ? response.data.eventReqs.Items
            : Array.isArray(response.data?.Items)
              ? response.data.Items
              : [];

        dispatch({ type: "setRequestData", payload: items });
      } catch (error) {
        console.error("GET error:", error);
        showNotification("Failed to load event requests", "error");
      } finally {
        if (isMounted) {
          dispatch({ type: "setLoading", payload: false });
        }
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, [showNotification]);

  return (
    <Layout
      navTitle="Event Requests"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search across all fields..."
            searchTerm={displaySearchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="request-list"
            excelSheetName="Event Requests"
          />
        }
      >
        <Table
          className="tpl-table"
          rowKey={(record) => record.id || record.request_id || record.event_id}
          rowClassName={(record, index) => (index % 2 === 0 ? "table-row-light" : "table-row-dark")}
          columns={columns}
          dataSource={currentPageData}
          loading={isLoading}
          pagination={false}
          expandable={{
            showExpandColumn: false
          }}
          onChange={handleTableChange}
        />
        <div className="tpl-pagination-wrapper">
          <Pagination className="tpl-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>

      {/* Date Range Modal */}
      <Modal
        title={`Select ${dateRangeType === "application" ? "Application" : "Event"} Date Range`}
        open={showDateRangeModal}
        onCancel={handleDateRangeModalCancel}
        footer={[
          <Button key="cancel" onClick={handleDateRangeModalCancel}>
            Cancel
          </Button>,
          <Button key="apply" onClick={applyDateRangeFilter}>
            Apply Filter
          </Button>
        ]}
        width={450}
        centered={false}
        style={{ top: 120 }}
      >
        <div style={{ marginBottom: "20px" }}>
          <RangePicker
            style={{ width: "100%" }}
            value={tempFromDate && tempToDate ? [dayjs(tempFromDate), dayjs(tempToDate)] : []}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            placeholder={["From Date", "To Date"]}
          />
        </div>
      </Modal>
    </Layout>
  );
}

export default RequestList;
